import {
  Component,
  computed,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { BaseStitchListComponent, StitchListItem } from '@modules/common/components/list/base-stitch-list.component';
import { ScrollToPosition } from '@modules/common/services/scroll-to-index.injection-token';
import { type StitchCardSelection, type StitchCardVariant } from '@modules/common/utils/stitch-card-variant';
import { type SelectableOpenEvent } from '@modules/drag-n-drop/types/selectable-open-event';
import { MessageFolderComponent } from '@modules/elements/components/message-folder/message-folder.component';
import { LinkedInfoService } from '@modules/linked-info/services/linked-info.service';
import { MailAppStateService } from '@modules/messages/services/mail-app-state.service';
import { MailboxTenantScopeService } from '@modules/common/services/mailbox-tenant-scope.service';
import { MessageFoldersService } from '@modules/messages/services/message-folders.service';
import { MessageFolder } from '@modules/messages/types/message-folder';
import { MessageFoldersFilters } from '@modules/messages/types/message-folders-filters';
import type { KnotSource } from '@sharedlib/common/types/knowledge/knot-source';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { MessageFolderSummaryDto } from '@sharedlib/dtos/dto/mail-app/folder.dto';
import { MessageSummaryDto } from '@sharedlib/dtos/dto/mail-app/message.dto';
import { BehaviorSubject, merge, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-message-folders-list',
  templateUrl: './folders-list.component.html',
  styleUrls: ['./folders-list.component.less'],
  providers: [{ provide: ScrollToPosition, useFactory: () => new BehaviorSubject<number>(null) }],
  standalone: false,
})
export class MessageFoldersListComponent extends BaseStitchListComponent<MessageFolder> implements OnChanges {
  public readonly tenantItems = input<ReadonlyArray<{ readonly accountId?: string }>>();

  /** Show each row's hover quick actions; off for lists that only preview their items. */
  public readonly actionsButtonEnabled = input<boolean>(true);

  /** Container treatment forwarded to every card; the knowledge panel asks for `'card'`. */
  public readonly variant = input<StitchCardVariant>('row');

  /** Id of the item open in the dynamic panel, forwarded so that card draws itself as selected. */
  public readonly previewedItemId = input<string | null>(null);

  /** What each card draws as selected, forwarded; the knowledge panel asks for the list's own selection. */
  public readonly cardSelection = input<StitchCardSelection>('preview');

  /** The knowledge panel draws its results as cards; every other caller keeps the app's flush row. */
  protected readonly isCard = computed(() => this.variant() === 'card');

  @Input()
  public override filters: Partial<MessageFoldersFilters> = {};
  @Input()
  public withTags = false;
  @Input()
  public withKnots = false;
  @Input()
  public knotsSource: KnotSource;
  @Input()
  public debug: 'score';

  @Output()
  public readonly clickFolder = new EventEmitter<MessageFolderSummaryDto>();
  @Output()
  public readonly openFolder = new EventEmitter<MessageFolderSummaryDto>();
  @Output()
  public readonly openMessage = new EventEmitter<MessageSummaryDto>();
  @Output()
  public readonly doubleClickFolder = new EventEmitter<MessageFolderSummaryDto>();

  @ViewChildren(MessageFolderComponent, { read: ElementRef<HTMLElement> })
  public override itemElements: QueryList<ElementRef<HTMLElement>>;

  public override items: StitchListItem<MessageFolder>[] = [];
  private readonly mailboxTenantScopeService = inject(MailboxTenantScopeService, { optional: true });

  protected override stitchSignalSpaceIds(): ReadonlyArray<string> {
    const spaceIds = new Set<string>([this.spaceContextStore.contextId()]);
    const effectiveTenantId = this.mailboxTenantScopeService?.effectiveTenantId();
    if (effectiveTenantId) {
      spaceIds.add(effectiveTenantId);
    }
    for (const { accountId } of this.tenantItems() ?? []) {
      if (accountId) {
        spaceIds.add(accountId);
      }
    }
    return [...spaceIds];
  }

  protected override getStitchType(): StitchType {
    return StitchType.MESSAGE_FOLDER;
  }

  constructor(
    private foldersService: MessageFoldersService,
    private messagesStateService: MailAppStateService,
    private linkedInfoService: LinkedInfoService,
  ) {
    super();

    merge(
      this.messagesStateService.getRefreshAll(),
      this.linkedInfoService.getRefresh(),
      this.foldersService.getRefresh(),
    )
      .pipe(takeUntil(this.alive))
      .subscribe(() => {
        this.loadPage(this.page);
      });
  }

  public ngOnChanges(changes: SimpleChanges) {
    if ('filters' in changes && !changes.filters.isFirstChange()) {
      this.resetItems();
    }
  }

  protected override getItems(offset: number, limit: number): Observable<{ items: MessageFolder[]; count: number }> {
    return this.foldersService.search(
      { ...this.filters, limit, offset },
      { withTags: this.withTags, withKnots: this.withKnots, withConnections: false, knotsSource: this.knotsSource },
    );
  }

  public handleDoubleClickItem(folder: MessageFolder) {
    this.doubleClickFolder.emit(folder);
  }

  public handleOpenSelected(event: SelectableOpenEvent<MessageFolder>): void {
    this.clickFolder.emit(event.item.data);
  }
}
